/**
 * Auth gate wrapping the adopted OmniBeta app shell.
 *
 * Ported/adapted from apps/frontend/src-legacy/App.tsx's root auth flow
 * (2026-07 adoption). Responsibilities:
 *  - While MSAL is handling a redirect back from Microsoft → loading screen.
 *  - Not signed in → show the branded login screen; clicking the button
 *    triggers an interactive SSO redirect.
 *  - Signed in → render the adopted app (children).
 *
 * `authRuntimeConfig.enabled === false` (VITE_AUTH_ENABLED=false) bypasses
 * all of this for local dev, matching src-legacy's behaviour.
 *
 * NOTE (2026-07-23): the previous `ssoSilent()` "check for an existing
 * browser session" step was removed. It ran an iframe-based silent request
 * to Microsoft on every load, which stalled (and under third-party-cookie
 * restrictions, errored) before the user could do anything. A cached local
 * account still signs the user straight in via MSAL's localStorage cache;
 * with no cached account we now go straight to the login screen and let the
 * button start the redirect. One click → SSO.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { authRuntimeConfig } from './config.js';
import { loginRequest } from './msal.js';
import LoginPage from './LoginPage.jsx';

function LoadingScreen() {
  return (
    <section className="loading-shell" aria-live="polite">
      <div className="loading-card">
        <p className="muted">Preparing secure session…</p>
      </div>
    </section>
  );
}

export default function LoginGate({ children }) {
  const [authError, setAuthError] = useState(null);

  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    if (instance.getActiveAccount() || accounts.length === 0) return;
    instance.setActiveAccount(accounts[0]);
  }, [instance, accounts]);

  const authLoading =
    authRuntimeConfig.enabled &&
    (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect);

  const handleSignIn = useCallback(async () => {
    try {
      setAuthError(null);
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'Unable to start sign-in.');
    }
  }, [instance]);

  if (!authRuntimeConfig.enabled) {
    return children;
  }

  if (authLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <LoginPage authError={authError} onSignIn={handleSignIn} />;
  }

  return children;
}
